<template>
  <div class="owc-hotspot-panorama">
    <div class="owc-hotspot-panorama__inner">
      <HotspotOverlay
        v-if="shouldRenderOverlay"
        class="owc-hotspot__overlay"
        :isDarkMode="isDarkMode"
        :title="overlay.title"
        :subtitle="overlay.subtitle"
        :isReActivated="isTwoFinger"
        @overlayOpen="openHotspotOverlay"
        @overlayClose="closeHotspotOverlay"
      >
        <template v-if="isMobile">
          <wb-icon class="owc-hotspot__overlay-icon" aria-hidden="true" name="bds/two-finger/24"></wb-icon>
        </template>
        <template v-else>
          <wb-icon class="owc-hotspot__overlay-icon" aria-hidden="true" name="bds/view-360/24"></wb-icon>
        </template>
      </HotspotOverlay>
      <div
        v-if="!imageLoaded && image"
        :class="{
          'owc-hotspot-panorama__loader': true,
          'owc-hotspot-panorama__loader--dark': isDarkMode,
        }"
      >
        <wb-spinner :theme="isDarkMode ? 'dark' : 'light'"></wb-spinner>
        <span v-if="loadingLabel" class="wb-type-copy-secondary owc-hotspot-panorama__loader-label">
          {{ loadingLabel }}
        </span>
      </div>
      <div
        role="img"
        ref="image360"
        :class="{
          'owc-hotspot-panorama__image-container': true,
          'owc-hotspot-panorama__image-container--hidden': !imageLoaded,
        }"
        :aria-label="imageAriaLabel"
      ></div>
    </div>
    <template v-if="isEditMode">
      <ValidationBoxWrapper>
        <slot slot="validation-box" name="validation-box" />
      </ValidationBoxWrapper>
      <SlotContainer
        refName="slot-hotspotAuthorPanoramaItems"
        ref="hotspotSlotContainer"
        class="owc-hotspot-panorama__author-items"
      >
        <slot></slot>
      </SlotContainer>
    </template>
    <transition name="owc-sidepanel-move-in-out" mode="in-out">
      <HotspotSidepanel
        v-if="isSidepanelOpen"
        ref="sidepanel"
        :sidePanelOpen="isSidepanelOpen"
        :hotspots="availableHotspots"
        :activeHotspot="activeHotspot"
        :leftHotspot="leftHotspot"
        :rightHotspot="rightHotspot"
        @sidepanel-close-click="onCloseSidepanelClick"
        @sidepanel-left-click="onLeftHotspotActivated"
        @sidepanel-right-click="onRightHotspotActivated"
        @sidepanel-mounted="centerViewer"
      />
    </transition>
    <transition name="owc-hotspot-background-fade-in-out" mode="in-out">
      <div class="owc-hotspot-panorama__background" v-if="isSidepanelOpen"></div>
    </transition>
  </div>
</template>

<script lang="ts">
/**
 * TODO: Documentation
 * HotspotPanorama
 *
 * Component to place hotspots on a 360 degree image.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import HotspotOverlay from './HotspotOverlay.vue';
import { mobileBreakpoints } from '@owc-utils/MediaQueryService';
import HotspotMixin from '@owc-utils/Mixins/HotspotMixin';
import HotspotPanoramaItem from './HotspotPanoramaItem.vue';
import { removeSlot, showOverlayAgain } from './HotspotPanoramaMethods';
import 'pannellum/src/js/pannellum';
import 'pannellum/src/js/libpannellum';
import {
  PANORAMA_MOBILE_HFOV,
  PANORAMA_DESKTOP_HFOV,
  PANNELLUM_CONFIG,
  PANNELLUM_MESSAGE_CONTAINER_SELECTORS,
  PANNELLUM_MESSAGE_SHOWN_EVENT,
  PANORAMA_ANIMATION_DURATION,
  PANORAMA_SIDEPANEL_WIDTH,
} from './Hotspot.config';
import { HotspotPanoramaComponent } from './HotspotPanorama.d';
import { HotspotPanoramaItemProps } from './HotspotPanoramaItem.d';
import HotspotSidepanel from './HotspotSidepanel.vue';
import { formatPropToAttribute, setFootnotes } from '@owc-packages/Hotspot/src/HotspotUtils';

export default (ComponentInitializeDispatcher as HotspotPanoramaComponent).extend({
  name: 'owc-hotspot-panorama',
  mixins: [HotspotMixin],
  components: {
    SlotContainer,
    ValidationBoxWrapper,
    HotspotOverlay,
    HotspotSidepanel,
  },
  props: {
    noOverlay: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    imageLoaded: false,
    isMobile: false,
    isTwoFinger: false,
    activeHotspot: null,
    isHotspotSidepanelActive: false,
    isPanoramaAnimationInProgress: false,
    isRtl: false,
  }),
  mounted() {
    if (this.componentId) {
      this.addHotspotChangeListener(this.componentId, this.findActiveHotspot);
      this.hideHotspotTabs(this.componentId);
    }
    if (!this.imageLoaded) {
      this.createImage();
      this.onViewportChanged();
      this.onMQChange?.(this.onViewportChanged);
    }
    this.isRtl = !!this.$el.closest('[dir="rtl"]');
  },
  beforeDestroy() {
    this.removeHotspotChangeListener(this.componentId, this.findActiveHotspot);
  },
  computed: {
    isDarkMode() {
      return Boolean(this.componentData?.darkModeOverlayBackground);
    },
    overlay() {
      return this.componentData?.overlay;
    },
    shouldRenderOverlay() {
      // Force the overlay to be hidden, e.g. for visual tests
      if (this.noOverlay) {
        return false;
      }
      return Boolean(this.overlay) && !this.isEditMode && this.imageLoaded;
    },
    isSidepanelOpen() {
      return Boolean(this.activeHotspot) && this.isHotspotSidepanelActive;
    },
    availableHotspots() {
      return (
        (this.componentData?.items || []).map(item => ({
          ...item,
          sidepanel: { ...item.sidepanel, component: this },
          pitch: parseFloat(item.pitch),
          yaw: parseFloat(item.yaw),
          id: item.identifiers.componentId,
        })) || []
      );
    },
    activeHotspotIndex() {
      if (this.activeHotspot) {
        return this.availableHotspots.findIndex(({ id }) => id === this.activeHotspot?.id);
      }
      return 0;
    },
    leftHotspot() {
      if (!this.activeHotspot) {
        return null;
      }
      let leftHotspotIndex = this.activeHotspotIndex - 1;
      if (leftHotspotIndex < 0) {
        leftHotspotIndex = this.availableHotspots.length - 1;
      }

      return this.availableHotspots[leftHotspotIndex];
    },
    rightHotspot() {
      if (!this.activeHotspot) {
        return null;
      }
      const rightHotspotIndex = (this.activeHotspotIndex + 1) % this.availableHotspots.length;

      return this.availableHotspots[rightHotspotIndex];
    },
    loadingLabel() {
      return this.componentData?.loadingLabel || '';
    },
    image() {
      return this.componentData?.image;
    },
    imageAriaLabel() {
      return this.image?.title || '';
    },
  },
  methods: {
    /**
     * Create the image by calling the Pannellum viewer.
     * On successful loading the wb-skeleton is hidden and Pannellum about message is removed.
     */
    createImage() {
      const wrapper = this.$refs.image360;
      this.viewer = window.pannellum.viewer(wrapper, {
        panorama: this.componentData?.image.source,
        hotSpots: this.isEditMode ? [] : this.generateItems(),
        ...PANNELLUM_CONFIG,
      });
      this.viewer.on('load', () => {
        this.imageLoaded = true;
        this.componentId && this.showHotspotTabs(this.componentId);

        removeSlot(wrapper, PANNELLUM_MESSAGE_CONTAINER_SELECTORS);

        // https://jira.mercedes-benz.io/browse/CINOWF-30499
        // It seems that in web-components build the clientHeigth of image360-Element is `0`
        // until the image is laoded and displayed.
        // Unfortunately `pannellum` fires a `load` event when it is loaded, but before it is rendered.
        // https://github.com/mpetroff/pannellum/blob/2.5.6/src/js/pannellum.js#L1680
        // That's why it is necessary to wait unit the image is "rendered" and clientHeight is
        // set to the right dimension. After the clientHeight is calculated/set correctly
        // the resize of the draw buffer is possible.
        this.$nextTick(() => {
          const desiredHeight = wrapper.clientHeight;
          const canvasHeight = this.viewer.getRenderer().getCanvas().height;

          if (canvasHeight < desiredHeight) {
            // need to trigger the resize function to trigger the reset of
            // draw buffer dimension
            this.viewer.resize();
          }
        });
      });
      showOverlayAgain(
        {
          showHotspotTabs: this.showHotspotTabs,
          hideHotspotTabs: this.hideHotspotTabs,
          viewer: this.viewer,
          componentId: this.componentId || '',
          isTwoFinger: this.isTwoFinger,
          isSidepanelOpen: this.isSidepanelOpen,
        },
        PANNELLUM_MESSAGE_SHOWN_EVENT,
      );
    },
    onViewportChanged() {
      const currentMq = this.getCurrentMediaQuery?.();
      this.isMobile = Boolean(currentMq && mobileBreakpoints.includes(currentMq));
      if (this.isMobile) {
        this.viewer.setHfov(PANORAMA_MOBILE_HFOV);
        this.viewer.setHfovBounds([PANORAMA_MOBILE_HFOV, PANORAMA_MOBILE_HFOV]);
      } else {
        this.viewer.setHfov(PANORAMA_DESKTOP_HFOV);
        this.viewer.setHfovBounds([PANORAMA_MOBILE_HFOV, PANORAMA_DESKTOP_HFOV]);
      }
    },
    generateItems() {
      const createTooltipFunc = (wrapper: HTMLElement, propsData: HotspotPanoramaItemProps): void => {
        // For WC build we need to create custom element and pass props to it as attributes;
        // Otherwise just create Vue component and append to wrapper.
        if (this.isBuildWc) {
          const item = document.createElement('owc-hotspot-panorama-item');
          for (const [attrName, attrValue] of Object.entries(propsData)) {
            item.setAttribute(formatPropToAttribute(attrName), attrValue?.toString() || '');
          }
          wrapper.appendChild(item);
        } else {
          const item = new HotspotPanoramaItem({ propsData });
          wrapper.appendChild(item.$mount().$el);
        }
      };
      return (
        this.availableHotspots.map(({ identifiers, pitch, yaw, hotspotText }) => ({
          pitch: pitch,
          yaw: yaw,
          cssClass: 'owc-hotspot-panorama__item', // dummy class to override Pannellum default classes
          createTooltipArgs: { hotspotText, ...identifiers },
          createTooltipFunc,
          id: identifiers.componentId,
        })) || []
      );
    },
    findActiveHotspot(state) {
      const activeHotspotId = this.componentId && state.activeItemsMap[this.componentId];

      // No active hotspot item id for the given module, just unset data and skip
      if (!activeHotspotId) {
        this.activeHotspot = null;
        this.isHotspotSidepanelActive = false;
        return;
      }

      // Skip when active hotspot in store is the same as the component's one
      if (activeHotspotId === this.activeHotspot?.id) {
        return;
      }

      const nextActiveHotspot = this.availableHotspots.find(({ id }) => id === activeHotspotId);

      if (nextActiveHotspot) {
        this.activeHotspot = nextActiveHotspot;
        this.isHotspotSidepanelActive = true;
      }
    },
    onCloseSidepanelClick() {
      this.setHotspotItem(null);
      this.activeHotspot = null;
      this.isHotspotSidepanelActive = false;
    },
    onLeftHotspotActivated() {
      if (!this.isPanoramaAnimationInProgress) {
        this.deactivateHotspotTooltip();
        this.activeHotspot = this.leftHotspot;
      }
    },
    onRightHotspotActivated() {
      if (!this.isPanoramaAnimationInProgress) {
        this.deactivateHotspotTooltip();
        this.activeHotspot = this.rightHotspot;
      }
    },
    /**
     * Will deactivate the tooltip when the image is moving
     * to the next hotspot item. If the tooltip is active
     * while the image is moving it looks like a bug
     */
    deactivateHotspotTooltip() {
      // set the active hotspot id to a non-existing value but NOT null!
      // this will toogle the tooltip.
      if (this.activeHotspot) {
        this.setHotspotItem(`${this.activeHotspot.id}-hidden-tooltip`);
      }
    },
    setHotspotItem(itemId) {
      this.setActiveHotspotId(this.componentId, itemId);
    },
    centerViewer() {
      if (this.activeHotspot) {
        const { pitch, yaw } = this.activeHotspot;
        const setYawCallback = () => {
          if (this.activeHotspot) {
            this.setHotspotItem(this.activeHotspot.id);
            this.isPanoramaAnimationInProgress = false;
          }
        };

        this.isPanoramaAnimationInProgress = true;
        this.viewer.setPitch(pitch, PANORAMA_ANIMATION_DURATION);

        if (this.isMobile) {
          this.viewer.setYaw(yaw, PANORAMA_ANIMATION_DURATION, setYawCallback);
        } else if (this.$refs.sidepanel) {
          const delta = Math.round(
            (100 * this.$el.clientWidth * PANORAMA_SIDEPANEL_WIDTH) / this.$el.clientWidth / 2,
          );
          const offset = isNaN(delta) ? 0 : this.isRtl ? -delta : delta;
          this.viewer.setYaw(yaw + offset, PANORAMA_ANIMATION_DURATION, setYawCallback);
        }
      }
    },
    openHotspotOverlay() {
      this.componentId && this.hideHotspotTabs(this.componentId);
    },
    closeHotspotOverlay() {
      this.componentId && this.showHotspotTabs(this.componentId);
    },
  },
  watch: {
    isSidepanelOpen(isOpen) {
      if (isOpen) {
        this.viewer.setHfov(PANORAMA_DESKTOP_HFOV - 10);
      } else {
        this.viewer.setHfov(this.isMobile ? PANORAMA_MOBILE_HFOV : PANORAMA_DESKTOP_HFOV);
      }
    },
    activeHotspot(hotspot) {
      if (hotspot) {
        this.componentId && this.hideHotspotTabs(this.componentId);
        this.centerViewer();
      } else {
        this.componentId && this.showHotspotTabs(this.componentId);
      }
    },
    availableHotspots: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          setFootnotes(newValue);
        }
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss">
@import '~pannellum/src/css/pannellum.css';
@import '@owc-styles/utils/z-index-list';

$moduleName: 'owc-hotspot-panorama';
$hotSpotSize: 40px;

.#{$moduleName} {
  @include wb-content-width;

  position: relative;

  width: 100%;

  margin-bottom: var(--wb-spacing-xl);

  &__inner {
    position: relative;

    display: block;

    width: 100%;

    overflow: hidden;

    &::before {
      display: block;

      padding-top: math.div(250% * 9, 16);

      content: '';

      @include breakpoint-from(mq3) {
        padding-top: math.div(100% * 9, 16);
      }
    }
  }
  &__loader {
    position: absolute;
    top: 0;
    left: 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: 100%;

    &--dark {
      color: var(--wb-white);

      background-color: var(--wb-black);
    }
    &-label {
      margin-top: 16px;

      color: inherit;
    }
  }
  &__image-container {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    overflow: hidden;

    direction: ltr; // AESISU-1133 this needs to be added to have correct placement of hotspot items, see https://github.com/mpetroff/pannellum/issues/903

    outline: none;

    &--hidden {
      position: absolute;

      opacity: 0;
    }
  }
  &__overlay-icon {
    fill: currentColor;
  }
  &__background {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    background-color: rgba($wb-black, 0.4);
  }
  &__item {
    width: $hotSpotSize;
    height: $hotSpotSize;
  }
  .owc-hotspot-background-fade-in-out {
    &-leave,
    &-enter-to {
      opacity: 1;
    }
    &-enter,
    &-leave-to {
      opacity: 0;
    }
    &-leave-active,
    &-enter-active {
      transition: opacity 0.35s $wb-fade;
    }
  }
  .owc-sidepanel-move-in-out {
    &-leave,
    &-enter-to {
      transform: translateX(0);
      opacity: 1;
    }
    &-enter,
    &-leave-to {
      transform: translateX(100%);
      opacity: 0;
    }
    &-leave-active {
      transition: transform 0.3s $wb-open, opacity 0.35s $wb-fade;
    }
    &-enter-active {
      transition: transform 0.3s $wb-open, opacity 0.35s 0.1s $wb-fade;
    }
  }
}
</style>
